import {SyncStatus} from '../offline/entity.js';
import {
  JOURNAL_LOCAL_ENTITY_TYPES,
  isJournalLocalEntityType,
  journalSyncPriority
} from './offline.js';
import {SMOKING_SESSION_ENTITY_TYPE} from './session-store.js';
import {SMOKING_STAGE_ENTITY_TYPE} from './stage-store.js';
import {LIVE_EVENT_ENTITY_TYPE} from '../live/live-event-store.js';
import {LIVE_STAGE_TRANSITION_ENTITY_TYPE} from '../live/stage-transition-store.js';

const ACTION_BY_STATUS=Object.freeze({
  [SyncStatus.PENDING_CREATE]:'create',
  [SyncStatus.PENDING_UPDATE]:'update',
  [SyncStatus.PENDING_DELETE]:'delete'
});

function orderPending(rows=[]){
  const sourceIds=new Set(
    rows
      .filter(row=>row.entityType===SMOKING_SESSION_ENTITY_TYPE&&row.sourceSessionId)
      .map(row=>String(row.sourceSessionId))
  );
  const sessionUpdatesByActiveStage=new Map(
    rows
      .filter(row=>row.entityType===SMOKING_SESSION_ENTITY_TYPE
        &&row.syncStatus===SyncStatus.PENDING_UPDATE
        &&row.activeStageId)
      .map(row=>[String(row.activeStageId),String(row.id)])
  );

  function priority(row){
    let value=journalSyncPriority(row.entityType)
      -(row.entityType===SMOKING_SESSION_ENTITY_TYPE&&sourceIds.has(String(row.id))?1:0);

    // A newly inserted custom Live stage must exist remotely before an already
    // synced session can update active_stage_id to reference it.
    if(row.entityType===SMOKING_STAGE_ENTITY_TYPE
      &&row.syncStatus===SyncStatus.PENDING_CREATE
      &&sessionUpdatesByActiveStage.has(String(row.id))){
      value=Math.min(value,journalSyncPriority(SMOKING_SESSION_ENTITY_TYPE)-1);
    }

    // Stage-transition rows form the accepted mutation chain. The companion
    // stage_changed event is evidence about an already-persisted transition,
    // so it must never reach the server before that transition chain.
    if(row.entityType===LIVE_STAGE_TRANSITION_ENTITY_TYPE){
      value=Math.min(value,70);
    }
    if(row.entityType===LIVE_EVENT_ENTITY_TYPE&&row.eventType==='stage_changed'){
      value=Math.max(value,85);
    }
    return value;
  }

  function transitionTime(row){
    if(row?.entityType!==LIVE_STAGE_TRANSITION_ENTITY_TYPE)return '';
    return String(row.transitionedAt||'');
  }

  return [...rows].sort((left,right)=>
    priority(left)-priority(right)
    ||(
      left?.entityType===LIVE_STAGE_TRANSITION_ENTITY_TYPE
      &&right?.entityType===LIVE_STAGE_TRANSITION_ENTITY_TYPE
        ?transitionTime(left).localeCompare(transitionTime(right))
        :0
    )
    ||String(left?.updatedAt||left?.createdAt||'').localeCompare(String(right?.updatedAt||right?.createdAt||''))
    ||String(left?.id||'').localeCompare(String(right?.id||''))
  );
}

function stageStateDependency(entity){
  if(entity?.entityType===LIVE_STAGE_TRANSITION_ENTITY_TYPE){
    return String(entity.sessionId||'')||null;
  }
  if(entity?.entityType===LIVE_EVENT_ENTITY_TYPE&&entity?.eventType==='stage_changed'){
    return String(entity.sessionId||'')||null;
  }
  return null;
}

function sessionConflictVersion(entity){
  return Number(entity?.conflict?.remoteServerVersion)||0;
}

function requireDependencies(repository,queue,remoteAdapters){
  if(!repository?.put||!repository?.remove)throw new TypeError('repository with put/remove is required');
  if(!queue?.listReady||!queue?.markAttempt||!queue?.markFailure||!queue?.markConflict){
    throw new TypeError('retry-aware sync queue contract is required');
  }
  if(!remoteAdapters||typeof remoteAdapters!=='object')throw new TypeError('remoteAdapters are required');
}

function syncedEntity(entity,result={}){
  const remote=result.entity&&typeof result.entity==='object'?result.entity:{};
  return {
    ...entity,
    ...remote,
    id:entity.id,
    syncStatus:SyncStatus.SYNCED,
    serverVersion:Number(result.serverVersion??remote.serverVersion??entity.serverVersion??0)||0,
    syncAttempts:0,
    lastSyncAttemptAt:entity.lastSyncAttemptAt||null,
    conflict:null,
    lastSyncFailureAt:null,
    lastSyncErrorCode:null,
    nextSyncAttemptAt:null
  };
}

export function createJournalSyncProcessor({
  repository,
  queue,
  remoteAdapters,
  maxBatch=100
}={}){
  requireDependencies(repository,queue,remoteAdapters);
  const batchLimit=Number(maxBatch);
  if(!Number.isInteger(batchLimit)||batchLimit<1||batchLimit>1000){
    throw new TypeError('maxBatch must be between 1 and 1000');
  }

  async function processOne(entity){
    const type=entity?.entityType;
    if(!isJournalLocalEntityType(type)){
      return Object.freeze({status:'ignored',entityType:type||null,id:entity?.id||null});
    }
    if(entity.syncStatus===SyncStatus.CONFLICT){
      return Object.freeze({status:'conflict_pending',entityType:type,id:entity.id});
    }

    const action=ACTION_BY_STATUS[entity.syncStatus];
    if(!action)return Object.freeze({status:'ignored',entityType:type,id:entity.id});

    const adapter=remoteAdapters[type];
    if(typeof adapter?.[action]!=='function'){
      return Object.freeze({status:'adapter_missing',entityType:type,id:entity.id,action});
    }

    const attempted=await queue.markAttempt(type,entity);
    try{
      const result=await adapter[action](attempted);

      if(result?.status==='conflict'){
        const conflicted=await queue.markConflict(type,attempted,{
          reason:String(result.reason||'version_mismatch'),
          remoteServerVersion:Number(result.remoteServerVersion)||0
        });
        return Object.freeze({status:'conflict',entityType:type,id:entity.id,entity:conflicted});
      }

      if(!result||result.ok!==true){
        const failed=await queue.markFailure(type,attempted,String(result?.errorCode||'REMOTE_RETRY_REQUESTED'));
        return Object.freeze({status:'retry',entityType:type,id:entity.id,action,entity:failed});
      }

      if(action==='delete'){
        await repository.remove(type,entity.id);
        return Object.freeze({status:'synced_delete',entityType:type,id:entity.id});
      }

      if(result.keepPendingUpdate===true&&action==='create'){
        const pending={
          ...attempted,
          syncStatus:SyncStatus.PENDING_UPDATE,
          serverVersion:Number(result.serverVersion)||1,
          syncAttempts:0,
          conflict:null,
          lastSyncFailureAt:null,
          lastSyncErrorCode:null,
          nextSyncAttemptAt:null
        };
        await repository.put(type,pending);
        return Object.freeze({status:'deferred_update',entityType:type,id:entity.id,entity:pending});
      }

      const synced=syncedEntity(attempted,result);
      await repository.put(type,synced);
      return Object.freeze({status:'synced',entityType:type,id:entity.id,entity:synced});
    }catch(error){
      const errorCode=String(error?.code||error?.name||'JOURNAL_SYNC_FAILED').slice(0,80);
      const failed=await queue.markFailure(type,attempted,errorCode);
      return Object.freeze({
        status:'retry',
        entityType:type,
        id:entity.id,
        action,
        errorCode,
        entity:failed
      });
    }
  }

  async function processPending(){
    const pending=await queue.listReady();
    const journalPending=(pending||[]).filter(entity=>JOURNAL_LOCAL_ENTITY_TYPES.includes(entity.entityType));
    const selected=orderPending(journalPending).slice(0,batchLimit);
    const blockedSessions=new Map(
      journalPending
        .filter(entity=>entity.entityType===SMOKING_SESSION_ENTITY_TYPE&&entity.syncStatus===SyncStatus.CONFLICT)
        .map(entity=>[String(entity.id),Object.freeze({
          reason:String(entity?.conflict?.reason||'version_mismatch'),
          remoteServerVersion:sessionConflictVersion(entity)
        })])
    );

    const results=[];
    const deferredByActiveStage=new Map();

    async function finishDeferredForStage(stageId){
      const items=deferredByActiveStage.get(String(stageId))||[];
      if(!items.length)return;
      deferredByActiveStage.delete(String(stageId));
      for(const item of items){
        const current=await repository.get(item.entityType,item.id);
        if(current?.syncStatus===SyncStatus.PENDING_UPDATE){
          results.push(await processOne(current));
        }
      }
    }

    for(const entity of selected){
      const dependentSessionId=stageStateDependency(entity);
      if(dependentSessionId&&blockedSessions.has(dependentSessionId)&&entity.syncStatus!==SyncStatus.CONFLICT){
        const parent=blockedSessions.get(dependentSessionId);
        const conflicted=await queue.markConflict(entity.entityType,entity,{
          reason:'parent_session_conflict',
          parentReason:parent.reason,
          remoteServerVersion:parent.remoteServerVersion
        });
        results.push(Object.freeze({
          status:'conflict_dependency',
          entityType:entity.entityType,
          id:entity.id,
          sessionId:dependentSessionId,
          entity:conflicted
        }));
        continue;
      }

      const result=await processOne(entity);
      results.push(result);

      if(entity.entityType===SMOKING_SESSION_ENTITY_TYPE
        &&(result.status==='conflict'||result.status==='conflict_pending')){
        const conflicted=result.entity||entity;
        blockedSessions.set(String(entity.id),Object.freeze({
          reason:String(conflicted?.conflict?.reason||'version_mismatch'),
          remoteServerVersion:sessionConflictVersion(conflicted)
        }));
      }

      if(result.status==='deferred_update'){
        const activeStageId=String(result.entity?.activeStageId||'');
        if(activeStageId){
          const list=deferredByActiveStage.get(activeStageId)||[];
          list.push(result);
          deferredByActiveStage.set(activeStageId,list);

          // The stage may already have been synced in an earlier batch.
          const stage=await repository.get(SMOKING_STAGE_ENTITY_TYPE,activeStageId);
          if(stage?.syncStatus===SyncStatus.SYNCED){
            await finishDeferredForStage(activeStageId);
          }
        }
      }

      // Finish a bootstrapped Live session immediately after its active stage
      // becomes remotely available, before temperature/events/notes are sent.
      if(entity.entityType===SMOKING_STAGE_ENTITY_TYPE
        &&(result.status==='synced'||result.status==='synced_delete')){
        await finishDeferredForStage(entity.id);
      }
    }

    return Object.freeze({
      processed:results.length,
      synced:results.filter(item=>item.status==='synced'||item.status==='synced_delete').length,
      deferred:results.filter(item=>item.status==='deferred_update').length,
      conflicts:results.filter(item=>['conflict','conflict_pending','conflict_dependency'].includes(item.status)).length,
      retries:results.filter(item=>item.status==='retry'||item.status==='adapter_missing').length,
      results:Object.freeze(results)
    });
  }

  return Object.freeze({processOne,processPending});
}

export function createJournalReconnectSync({
  journalProcessor,
  dependencyProcessors=[],
  eventTarget=globalThis,
  isOnline=()=>globalThis.navigator?.onLine!==false,
  onResult=()=>{}
}={}){
  if(!journalProcessor?.processPending)throw new TypeError('journalProcessor is required');
  for(const processor of dependencyProcessors){
    if(!processor?.processPending)throw new TypeError('dependency processor must expose processPending');
  }

  let started=false;
  let inFlight=null;

  async function run(){
    if(!isOnline())return Object.freeze({status:'offline',results:Object.freeze([])});
    if(inFlight)return inFlight;

    inFlight=(async()=>{
      const results=[];
      try{
        for(const processor of dependencyProcessors){
          results.push(await processor.processPending());
        }
        results.push(await journalProcessor.processPending());
        const summary=Object.freeze({status:'completed',results:Object.freeze(results)});
        try{onResult(summary);}catch{}
        return summary;
      }finally{
        inFlight=null;
      }
    })();

    return inFlight;
  }

  function onOnline(){
    void run();
  }

  function start({syncIfOnline=true}={}){
    if(started)return Object.freeze({started:true,alreadyStarted:true});
    if(typeof eventTarget?.addEventListener!=='function')throw new TypeError('eventTarget must support addEventListener');
    eventTarget.addEventListener('online',onOnline);
    started=true;
    if(syncIfOnline&&isOnline())void run();
    return Object.freeze({started:true,alreadyStarted:false});
  }

  function stop(){
    if(!started)return Object.freeze({stopped:true,alreadyStopped:true});
    eventTarget?.removeEventListener?.('online',onOnline);
    started=false;
    return Object.freeze({stopped:true,alreadyStopped:false});
  }

  return Object.freeze({run,start,stop});
}
